/**
 * Shared TPH (harvest) detail rows for the harvest monitoring reports.
 * Rendered as the expanded sub-rows under a block (per-location view) or a
 * kemandoran (per-foreman view). The cells are self-contained and auto-size
 * within the parent unstyled table, so the same component works for both
 * tables regardless of their main column count.
 */

import React from 'react';
import { Table, Tbody, Tr, Td, Text, Badge, HStack, IconButton, Image, Tooltip } from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import previewIcon from '../../../assets/icons/preview_icon.png';
import type { tph_details } from '../types';
import { formatNumber, formatNumberWithLineBreak } from '../utils';

interface TphDetailRowsProps {
    details: tph_details[];
    onPreviewImage: (imagePath: string | undefined, title: string) => void;
    /**
     * When set, the detail rows are wrapped in a single full-width cell spanning
     * `wrapColSpan` parent columns, rendered as a self-contained nested table.
     * Use this when the parent table has fewer columns than the detail (so the
     * detail stays within the parent width instead of spilling past it).
     * When omitted, the rows are emitted inline into the parent table grid
     * (original per-location behaviour).
     */
    wrapColSpan?: number;
}

export const TphDetailRows: React.FC<TphDetailRowsProps> = ({ details, onPreviewImage, wrapColSpan }) => {
    const rows = (
        <>
            <Tr>
                <Td></Td>
                <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" whiteSpace="nowrap" textAlign="left">MANDOR PANEN / KERANI PANEN</Text></Td>
                <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">PEMANEN</Text></Td>
                <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">NO. BCC</Text></Td>
                <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">TPH CODE</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">LUASAN PANEN</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right" >JML JJG</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">JJG MATANG</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">JJG MENTAH</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">JJG BUSUK & JANGKOS</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">KG BRONDOLAN</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">NO SPB</Text></Td>
                <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">TICKET NO</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">NO KENDARAAN</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">DRIVER</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">NO. BA</Text></Td>
                <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">TIPE BA</Text></Td>
            </Tr>

            {details.map((tph, tphIdx) => {
                const isNotSent = tph.is_send === 'TIDAK' || (tph.no_spb !== '' && !tph.ticket_no);

                return (
                    <Tr
                        key={`${tph.tph_code}-${tphIdx}`}
                        bg="white"
                        _hover={{ bg: 'gray.50' }}
                    >
                        <Td></Td>
                        <Td textAlign="left">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {tph.foreman_name} <br /> {tph.created_by}
                                </Badge>
                            ) : (
                                <Text fontSize="xs" color="gray.700">{tph.foreman_name} / <br /> {tph.created_by}</Text>
                            )}
                        </Td>

                        <Td textAlign="left">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {tph.harvester_name} <br /> {tph.harvester_nip}
                                </Badge>
                            ) : (
                                <Text fontSize="xs" color="gray.700">{tph.harvester_name} / <br /> {tph.harvester_nip}</Text>
                            )}
                        </Td>
                        <Td textAlign="left">
                            <HStack spacing={1}>
                                {isNotSent ? (
                                    <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                        {formatNumberWithLineBreak(tph.bcc_number)}
                                    </Badge>
                                ) : (
                                    <Text fontSize="xs" color="gray.700">{formatNumberWithLineBreak(tph.bcc_number)}</Text>
                                )}

                                {tph.bcc_image && (
                                    <IconButton
                                        aria-label="Preview BCC"
                                        icon={<Image src={previewIcon} alt="Preview" boxSize="16px" />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            onPreviewImage(tph.bcc_image, `${tph.bcc_number}`);
                                        }}
                                    />
                                )}

                            </HStack>

                        </Td>

                        <Td textAlign="left">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {tph.tph_code}
                                </Badge>
                            ) : (
                                <Text fontSize="xs" color="gray.700">{tph.tph_code}</Text>
                            )}
                        </Td>

                        <Td isNumeric textAlign="right">
                            {tph.ha_per_tph > 0 ? (
                                <Text fontSize="xs">{formatNumber(tph.ha_per_tph)}</Text>
                            ) : tph.bunch_qty > 0 ? (
                                <Tooltip label="Luasan panen (HA) kosong tetapi ada janjang panen!" placement="top" hasArrow>
                                    <span>
                                        <WarningIcon color="red.500" boxSize={4} verticalAlign="middle" />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                        </Td>

                        <Td isNumeric textAlign="right">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {formatNumber(tph.bunch_qty)}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{formatNumber(tph.bunch_qty)}</Text>
                            )}
                        </Td>

                        <Td isNumeric textAlign="right">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {formatNumber(tph.ripe_qty)}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{formatNumber(tph.ripe_qty)}</Text>
                            )}
                        </Td>

                        <Td isNumeric textAlign="right">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {formatNumber(tph.unripe_qty)}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{formatNumber(tph.unripe_qty)}</Text>
                            )}
                        </Td>

                        <Td isNumeric textAlign="right">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {formatNumber(tph.overripe_qty)}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{formatNumber(tph.overripe_qty)}</Text>
                            )}
                        </Td>

                        <Td isNumeric textAlign="right">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {formatNumber(tph.loose_fruit_qty)}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{formatNumber(tph.loose_fruit_qty)}</Text>
                            )}
                        </Td>

                        <Td textAlign="left">
                            <HStack spacing={1}>
                                {isNotSent ? (
                                    <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                        {formatNumberWithLineBreak(tph.no_spb)}
                                    </Badge>
                                ) : (
                                    <Text fontSize="xs">{formatNumberWithLineBreak(tph.no_spb)}</Text>
                                )}
                                {tph.spb_image && (
                                    <IconButton
                                        aria-label="Preview SPB"
                                        icon={<Image src={previewIcon} alt="Preview" boxSize="16px" />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            onPreviewImage(tph.spb_image, `${tph.no_spb}`);
                                        }}
                                    />
                                )}
                            </HStack>

                        </Td>

                        <Td textAlign="left">
                            {tph.ticket_no ? (
                                <Text fontSize="xs" color="green.600" fontWeight="semibold">{tph.ticket_no}</Text>
                            ) : (
                                <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                        </Td>

                        <Td textAlign="left">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {tph.transport_nbr}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{tph.transport_nbr}</Text>
                            )}
                        </Td>

                        <Td textAlign="left">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {tph.driver_name}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{tph.driver_name}</Text>
                            )}
                        </Td>

                        <Td textAlign="left">
                            <HStack spacing={1}>
                                {isNotSent ? (
                                    <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                        {formatNumberWithLineBreak(tph.ba_number)}
                                    </Badge>
                                ) : (
                                    <Text fontSize="xs">{formatNumberWithLineBreak(tph.ba_number)}</Text>
                                )}
                                {tph.ba_image && (
                                    <IconButton
                                        aria-label="Preview BA"
                                        icon={<Image src={previewIcon} alt="Preview" boxSize="16px" />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            onPreviewImage(tph.ba_image, `${tph.ba_number}`);
                                        }}
                                    />
                                )}
                            </HStack>

                        </Td>

                        <Td textAlign="left">
                            {isNotSent ? (
                                <Badge colorScheme="yellow" variant="subtle" fontSize="2xs">
                                    {tph.ba_type}
                                </Badge>
                            ) : (
                                <Text fontSize="xs">{tph.ba_type}</Text>
                            )}
                        </Td>
                    </Tr>
                );
            })}
        </>
    );

    if (wrapColSpan !== undefined) {
        return (
            <Tr>
                <Td colSpan={wrapColSpan} p={0}>
                    <Table variant="unstyled" size="sm">
                        <Tbody>
                            {rows}
                        </Tbody>
                    </Table>
                </Td>
            </Tr>
        );
    }

    return rows;
};
